import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { categories, categoryThumbnails } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const handleCategorySelect = useCallback((url: string) => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    window.open(url, "_blank");
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 h-[72px] flex items-center transition-all duration-300 ${
          scrolled ? "bg-background-50/95 backdrop-blur-sm" : "bg-transparent"
        }`}
      >
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between">
          <Link
            to="/"
            className="text-[15px] font-semibold tracking-[0.02em] text-foreground-900 font-heading whitespace-nowrap"
            onClick={closeMobileMenu}
          >
            CSSSpreadsheet.living
          </Link>

          <div className="hidden md:flex items-center gap-6 text-[13px] text-foreground-600">
            <Link to="/" className="hover:text-foreground-900 transition-colors duration-200 whitespace-nowrap flex items-center gap-1.5">
              <i className="ri-home-4-line text-sm w-4 h-4 flex items-center justify-center"></i>
              Home
            </Link>
            <a href="#categories" className="hover:text-foreground-900 transition-colors duration-200 whitespace-nowrap">
              Categories
            </a>
            <Link to="/article/shopping-guide" className="hover:text-foreground-900 transition-colors duration-200 whitespace-nowrap">
              Shopping Guide
            </Link>
            <Link to="/article/cssbuy-spreadsheet-ultimate-guide" className="hover:text-foreground-900 transition-colors duration-200 whitespace-nowrap">
              Guides
            </Link>
            <Link to="/article/best-agent-spreadsheets-2026" className="hover:text-foreground-900 transition-colors duration-200 whitespace-nowrap">
              Best Finds
            </Link>
            <a href="#explore" className="hover:text-foreground-900 transition-colors duration-200 whitespace-nowrap">
              Explore
            </a>
          </div>

          <div className="flex items-center gap-3">
            <div ref={dropdownRef} className="relative hidden md:block">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="h-10 px-4 flex items-center gap-2 bg-background-50 border border-secondary-300 rounded-full text-[13px] text-foreground-700 hover:border-foreground-300 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <span>Browse Categories</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-[300px] bg-background-50 border border-secondary-200 rounded-2xl shadow-lg overflow-hidden z-50 max-h-[480px] overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategorySelect(cat.url)}
                      className="w-full text-left px-4 py-3 flex items-center gap-3 text-[13px] text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors duration-150 cursor-pointer border-b border-background-200 last:border-b-0"
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                        <img
                          src={categoryThumbnails[cat.imageSeq]}
                          alt={cat.name}
                          className="w-full h-full object-cover object-top"
                          loading="lazy"
                        />
                      </div>
                      <div className="min-w-0">
                        <span className="block whitespace-nowrap font-medium">{cat.name}</span>
                        <span className="block text-[11px] text-foreground-500 whitespace-nowrap">{cat.description}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex h-10 px-5 items-center bg-foreground-900 text-background-50 rounded-full text-[13px] font-medium hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap"
            >
              cssbuy sheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex h-10 px-5 items-center bg-background-50 border border-secondary-300 text-foreground-800 rounded-full text-[13px] font-medium hover:border-foreground-400 transition-colors duration-200 whitespace-nowrap"
            >
              buy it now
            </a>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden h-10 w-10 flex items-center justify-center rounded-lg bg-background-50 border border-secondary-300 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-lg text-foreground-700 w-5 h-5 flex items-center justify-center ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={closeMobileMenu}></div>
          <div className="absolute top-0 right-0 w-[85%] max-w-[380px] h-full bg-background-50 shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-background-200">
              <span className="text-[16px] font-semibold text-foreground-900 font-heading">Menu</span>
              <button
                onClick={closeMobileMenu}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg text-foreground-700 w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>

            <div className="px-6 py-4 border-b border-background-100 flex flex-col gap-2">
              <Link
                to="/"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 py-3 text-[14px] font-medium text-foreground-800 hover:text-foreground-950 transition-colors duration-150"
              >
                <i className="ri-home-4-line text-base w-5 h-5 flex items-center justify-center"></i>
                Home
              </Link>
              <Link
                to="/article/shopping-guide"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 py-3 text-[14px] font-medium text-foreground-800 hover:text-foreground-950 transition-colors duration-150"
              >
                <i className="ri-book-read-line text-base w-5 h-5 flex items-center justify-center"></i>
                Shopping Guide
              </Link>
              <Link
                to="/article/cssbuy-spreadsheet-ultimate-guide"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 py-3 text-[14px] font-medium text-foreground-800 hover:text-foreground-950 transition-colors duration-150"
              >
                <i className="ri-article-line text-base w-5 h-5 flex items-center justify-center"></i>
                Ultimate Guide
              </Link>
              <Link
                to="/article/cssbuy-vs-kakobuy-comparison"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 py-3 text-[14px] font-medium text-foreground-800 hover:text-foreground-950 transition-colors duration-150"
              >
                <i className="ri-scales-line text-base w-5 h-5 flex items-center justify-center"></i>
                Agent Comparison
              </Link>
              <Link
                to="/article/best-agent-spreadsheets-2026"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 py-3 text-[14px] font-medium text-foreground-800 hover:text-foreground-950 transition-colors duration-150"
              >
                <i className="ri-star-line text-base w-5 h-5 flex items-center justify-center"></i>
                Best Finds 2026
              </Link>
            </div>

            <div className="px-6 py-4 border-b border-background-100">
              <span className="text-[11px] font-medium text-foreground-500 tracking-[0.04em] uppercase">Categories</span>
              <div className="mt-3 space-y-1">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategorySelect(cat.url)}
                    className="w-full text-left px-3 py-2.5 flex items-center gap-3 rounded-lg hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                  >
                    <div className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                      <img
                        src={categoryThumbnails[cat.imageSeq]}
                        alt={cat.name}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                      />
                    </div>
                    <div className="min-w-0">
                      <span className="block text-[13px] font-medium text-foreground-800">{cat.name}</span>
                      <span className="block text-[11px] text-foreground-500 leading-tight">{cat.description}</span>
                    </div>
                    <i className="ri-arrow-right-up-line text-foreground-400 text-sm w-4 h-4 flex items-center justify-center ml-auto flex-shrink-0"></i>
                  </button>
                ))}
              </div>
            </div>

            <div className="px-6 py-5 flex flex-col gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobileMenu}
                className="w-full h-12 flex items-center justify-center gap-2 bg-foreground-900 text-background-50 rounded-full text-[14px] font-medium hover:bg-foreground-800 transition-colors duration-200"
              >
                cssbuy sheet
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobileMenu}
                className="w-full h-12 flex items-center justify-center gap-2 bg-background-50 border border-secondary-300 text-foreground-800 rounded-full text-[14px] font-medium hover:border-foreground-400 transition-colors duration-200"
              >
                buy it now
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}